// 牌局結束三畫面（結算流程）共用資料層
//
// 三個 scene（ResultTile → ResultPoint → ResultMoney）共用同一份 payload：
//   - players：依 seat 排的玩家資訊（風、名字、手牌、是否莊家）
//   - chips：依 seat 排的籌碼結算（before / delta / after / 勝敗）
//
// payload 來源兩條路：
//   - 正式對局：game-scene round.ended → buildResultFlowData(...)（真實 summary/hands）
//   - 預覽模式：?scene=ResultTile|ResultPoint|ResultMoney → buildMockResultFlowData()
//
// 正式對局由永久帳本傳入真實籌碼；未傳入 chips 時才使用預覽專用假模型。

import { addToConcealed, createEmptyHand } from '@core/hand';
import { MeldType } from '@core/meld';
import { RoundSummaryKind } from '@core/summary';
import type { THuResult, TRoundSummary } from '@core/summary';
import type { THand } from '@core/hand';
import type { TMeld } from '@core/meld';
import type { TTile } from '@core/tile';
import { Seat, Suit, Wind } from '@core/types';
import type { TSeat, TWind } from '@core/types';

// ============================================================================
// 假籌碼模型常數
// ============================================================================
const CHIP_BASE = 1_000_000; // 每家起始假基金
const CHIP_DI = 1000; // 底（每次胡牌固定收）
const CHIP_TAI_UNIT = 400; // 台底（每台加收）；3 台 = 1000 + 3×400 = 2200

/** total 台 → 一注金額（底 + 台×台底）。 */
const chipAmountForTai = (totalTai: number): number => CHIP_DI + totalTai * CHIP_TAI_UNIT;

// ============================================================================
// 玩家名字常數表（沿用原版四名；依 seat 對應，玩家固定 seat 0）
// ============================================================================
export const RESULT_PLAYER_NAMES: Record<TSeat, string> = {
  [Seat.Self]: '市民',
  [Seat.Right]: '史豔文',
  [Seat.Opposite]: '水手金星',
  [Seat.Left]: '無敵鐵金剛',
};

// ============================================================================
// 型別
// ============================================================================

/** 結算畫面用的「勝 / 敗 / 無」三態。 */
export const ChipOutcome = {
  Win: 'win', // 贏（winner）→ 勝章
  Lose: 'lose', // 輸（付款者）→ 敗章
  None: 'none', // 未動（無章）
} as const;
export type TChipOutcome = (typeof ChipOutcome)[keyof typeof ChipOutcome];

/** 單一家的結算資訊（攤牌 / 算台 / 籌碼三畫面共用）。 */
export type TResultPlayerInfo = {
  readonly seat: TSeat;
  readonly wind: TWind;
  readonly name: string;
  readonly isDealer: boolean;
  readonly hand: THand;
};

/** 單一家的籌碼結算。 */
export type TResultChip = {
  readonly seat: TSeat;
  readonly before: number;
  readonly delta: number;
  readonly after: number;
  readonly outcome: TChipOutcome;
};

/** 三畫面共用 payload。 */
export type TResultFlowData = {
  readonly summary: TRoundSummary;
  /** 依 seat（0..3）排列。 */
  readonly players: ReadonlyArray<TResultPlayerInfo>;
  /** 依 seat（0..3）排列。 */
  readonly chips: ReadonlyArray<TResultChip>;
  /** 玩家所在 seat（決定「自家/下家/對家/上家」相對方位）。MVP 固定 Seat.Self。 */
  readonly playerSeat: TSeat;
};

// ============================================================================
// 假籌碼結算
// ============================================================================

/**
 * 由 summary 算預覽專用「假籌碼」結算（每家 before/delta/after + 勝敗）。
 * - 放槍：放槍者付 winner（底+台）；自摸：三家各付 winner；流局：四家不變。
 * 正式規則只會有一位 winner；保留陣列迴圈是為了相容舊預覽 payload 型別。
 */
export const computeFakeChips = (summary: TRoundSummary): ReadonlyArray<TResultChip> => {
  const delta: Record<TSeat, number> = {
    [Seat.Self]: 0,
    [Seat.Right]: 0,
    [Seat.Opposite]: 0,
    [Seat.Left]: 0,
  };

  if (summary.kind === RoundSummaryKind.Hu) {
    for (const r of summary.results) {
      const amount = chipAmountForTai(r.total);
      if (r.isSelfDraw) {
        // 自摸：其他三家各付 amount，winner 收 3×amount
        for (const s of [Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as TSeat[]) {
          if (s === r.seat) continue;
          delta[s] -= amount;
          delta[r.seat] += amount;
        }
      } else if (r.loserSeat !== null) {
        // 放槍：放槍者付 amount
        delta[r.loserSeat] -= amount;
        delta[r.seat] += amount;
      }
    }
  }

  return ([Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as TSeat[]).map((seat) => {
    const d = delta[seat];
    const outcome: TChipOutcome =
      d > 0 ? ChipOutcome.Win : d < 0 ? ChipOutcome.Lose : ChipOutcome.None;
    return { seat, before: CHIP_BASE, delta: d, after: CHIP_BASE + d, outcome };
  });
};

// ============================================================================
// 正式對局：由 round 結束資料組 payload
// ============================================================================

export type TBuildResultFlowInput = {
  readonly summary: TRoundSummary;
  readonly hands: ReadonlyArray<THand>;
  readonly seatWinds: ReadonlyArray<TWind>;
  readonly dealerSeat: TSeat;
  readonly playerSeat?: TSeat;
  /** 跨局帳本已原子結算後的真實籌碼；省略時只供預覽使用假資料。 */
  readonly chips?: ReadonlyArray<TResultChip>;
  /** 依座位的名字（牌局組裡的牌友角色）；省略時用原版四名。 */
  readonly names?: ReadonlyArray<string | undefined>;
};

export const buildResultFlowData = (input: TBuildResultFlowInput): TResultFlowData => {
  const playerSeat = input.playerSeat ?? Seat.Self;

  // 放槍胡：胡的那張牌只存在 summary.results[].winTile，引擎結算時不會塞進贏家手牌
  // （見 round.ts endRoundHu）；自摸的胡張則早已併入 concealed。攤牌前補進贏家手牌，
  // 否則放槍贏家會少一張、攤出不完整的聽牌型，看起來像沒胡。
  const discardWinTileBySeat = new Map<TSeat, TTile>();
  if (input.summary.kind === RoundSummaryKind.Hu) {
    for (const r of input.summary.results) {
      if (!r.isSelfDraw) discardWinTileBySeat.set(r.seat, r.winTile);
    }
  }
  const resolveHand = (seat: TSeat): THand => {
    const base = input.hands[seat] ?? createEmptyHand();
    const winTile = discardWinTileBySeat.get(seat);
    return winTile ? addToConcealed(base, winTile) : base;
  };

  const players: TResultPlayerInfo[] = (
    [Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as TSeat[]
  ).map((seat) => ({
    seat,
    wind: input.seatWinds[seat] ?? Wind.East,
    name: input.names?.[seat] ?? RESULT_PLAYER_NAMES[seat],
    isDealer: seat === input.dealerSeat,
    hand: resolveHand(seat),
  }));
  return {
    summary: input.summary,
    players,
    chips: input.chips ?? computeFakeChips(input.summary),
    playerSeat,
  };
};

export const buildResultChips = (
  balancesBefore: readonly [number, number, number, number],
  deltaBySeat: readonly [number, number, number, number]
): ReadonlyArray<TResultChip> =>
  ([Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as TSeat[]).map((seat) => {
    const delta = deltaBySeat[seat];
    return {
      seat,
      before: balancesBefore[seat],
      delta,
      after: balancesBefore[seat] + delta,
      outcome: delta > 0 ? ChipOutcome.Win : delta < 0 ? ChipOutcome.Lose : ChipOutcome.None,
    };
  });

// ============================================================================
// 預覽模式：固定 mock（自家東莊放槍給下家南，下家胡 3 台）
// ============================================================================

/**
 * 預覽模式專用固定 mock：
 * - 莊家＝自家（seat0，東）
 * - winner＝下家（seat1，南），由自家放槍
 * - 台數＝無字無花 2 台 + 門清 1 台 = 共 3 台
 */
export const buildMockResultFlowData = (): TResultFlowData => {
  let id = 0;
  const t = (suit: TTile['suit'], rank: number): TTile => ({ id: id++, suit, rank });
  const pong = (suit: TTile['suit'], rank: number): TMeld => ({
    type: MeldType.Pong,
    tiles: [t(suit, rank), t(suit, rank), t(suit, rank)],
  });

  // seat0（自家，東，莊，放槍者）：副露 北碰 + 一條順子手牌
  const handSelf: THand = {
    concealed: [
      t(Suit.Man, 3),
      t(Suit.Man, 4),
      t(Suit.Man, 5),
      t(Suit.Man, 7),
      t(Suit.Sou, 2),
      t(Suit.Sou, 2),
      t(Suit.Pin, 8),
      t(Suit.Pin, 9),
    ],
    melds: [pong(Suit.Wind, 4) /* 北碰 */],
    flowers: [t(Suit.Flower, 5) /* 梅 */],
  };

  // seat1（下家，南，winner，門清自摸型）：全暗一手好牌
  const handWinner: THand = {
    concealed: [
      t(Suit.Man, 2),
      t(Suit.Man, 3),
      t(Suit.Man, 4),
      t(Suit.Man, 6),
      t(Suit.Man, 7),
      t(Suit.Man, 8),
      t(Suit.Pin, 2),
      t(Suit.Pin, 3),
      t(Suit.Pin, 4),
      t(Suit.Sou, 5),
      t(Suit.Sou, 6),
      t(Suit.Sou, 7),
      t(Suit.Sou, 9),
      t(Suit.Sou, 9),
      t(Suit.Pin, 6),
      t(Suit.Pin, 7),
      t(Suit.Pin, 8) /* 胡張 */,
    ],
    melds: [],
    flowers: [],
  };

  // seat2（對家，西）：碰一坎 + 雜牌
  const handOpposite: THand = {
    concealed: [
      t(Suit.Man, 1),
      t(Suit.Man, 2),
      t(Suit.Man, 3),
      t(Suit.Sou, 4),
      t(Suit.Sou, 5),
      t(Suit.Pin, 1),
      t(Suit.Pin, 1),
      t(Suit.Dragon, 1),
    ],
    melds: [pong(Suit.Pin, 5) /* 五筒碰 */, pong(Suit.Sou, 8) /* 八條碰 */],
    flowers: [t(Suit.Flower, 1)],
  };

  // seat3（上家，北）：兩坎子 + 雜牌
  const handPre: THand = {
    concealed: [
      t(Suit.Pin, 2),
      t(Suit.Pin, 3),
      t(Suit.Man, 7),
      t(Suit.Man, 7),
      t(Suit.Man, 8),
      t(Suit.Sou, 1),
      t(Suit.Sou, 2),
      t(Suit.Wind, 1),
    ],
    melds: [pong(Suit.Pin, 6) /* 六筒碰 */, pong(Suit.Pin, 4) /* 四筒碰 */],
    flowers: [],
  };

  const winnerResult: THuResult = {
    seat: Seat.Right,
    isSelfDraw: false,
    loserSeat: Seat.Self,
    winTile: t(Suit.Pin, 8),
    scoreItems: [
      { key: 'wuziwuhua', points: 2 },
      { key: 'menqing', points: 1 },
    ],
    total: 3,
  };

  const summary: TRoundSummary = { kind: RoundSummaryKind.Hu, results: [winnerResult] };

  const players: TResultPlayerInfo[] = [
    {
      seat: Seat.Self,
      wind: Wind.East,
      name: RESULT_PLAYER_NAMES[Seat.Self],
      isDealer: true,
      hand: handSelf,
    },
    {
      seat: Seat.Right,
      wind: Wind.South,
      name: RESULT_PLAYER_NAMES[Seat.Right],
      isDealer: false,
      hand: handWinner,
    },
    {
      seat: Seat.Opposite,
      wind: Wind.West,
      name: RESULT_PLAYER_NAMES[Seat.Opposite],
      isDealer: false,
      hand: handOpposite,
    },
    {
      seat: Seat.Left,
      wind: Wind.North,
      name: RESULT_PLAYER_NAMES[Seat.Left],
      isDealer: false,
      hand: handPre,
    },
  ];

  return {
    summary,
    players,
    chips: computeFakeChips(summary),
    playerSeat: Seat.Self,
  };
};
